import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { ApiService } from './api.service';

@Component({
  selector: 'app-customer-invoice',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="min-h-screen bg-[#F7FAFC] text-slate-900 p-4 md:p-8">
      <div class="max-w-4xl mx-auto">
        @if (isLoading()) {
          <div class="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500">Loading invoice...</div>
        } @else if (!invoice()) {
          <div class="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500">Invoice not found.</div>
        } @else {
          <div class="bg-[#14384F] text-white rounded-xl p-5 mb-4 flex items-center justify-between">
            <div>
              <p class="text-[10px] uppercase tracking-[0.2em] text-[#8CCCEB] font-bold">NZ Plumbing</p>
              <h1 class="text-2xl font-semibold mt-1">Invoice {{ invoice().invoice_no }}</h1>
            </div>
            <span class="px-3 py-1 rounded-lg bg-white/10 border border-white/10 text-xs font-semibold uppercase">{{ invoice().status }}</span>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-[1fr_320px] gap-4">
            <main class="space-y-4">
              <section class="bg-white rounded-xl border border-[#E7EEF5] p-4">
                <p class="text-[10px] font-bold text-[#2B9AD2] uppercase tracking-wider mb-2">Bill To</p>
                <h2 class="text-lg font-semibold">{{ invoice().customer_name }}</h2>
                <p class="text-sm text-slate-500 mt-1">{{ invoice().billing_address }}</p>
              </section>

              <section class="bg-white rounded-xl border border-[#E7EEF5] overflow-hidden">
                <div class="px-4 py-3 bg-slate-50 border-b border-slate-100">
                  <h2 class="text-sm font-semibold uppercase tracking-wide">Invoice Items</h2>
                </div>
                <div class="divide-y divide-slate-100">
                  @for (item of invoice().items || []; track item.id) {
                    <div class="p-4 flex gap-3">
                      @if (item.photo_url) {
                        <img [src]="item.photo_url" class="w-10 h-10 rounded-lg object-cover border border-slate-100" alt="Material">
                      }
                      <div class="flex-1">
                        <p class="text-sm font-semibold">{{ item.title }}</p>
                        <p class="text-xs text-slate-500">{{ item.desc }}</p>
                        <p class="text-[11px] text-slate-400 mt-1">{{ item.qty }} {{ item.unit }} @ {{ item.price | currency }}</p>
                      </div>
                      <p class="text-sm font-semibold">{{ item.total_price | currency }}</p>
                    </div>
                  }
                </div>
              </section>

              <section class="bg-white rounded-xl border border-[#E7EEF5] p-4">
                <h2 class="text-sm font-semibold uppercase tracking-wide mb-3">Payment Record</h2>
                @if ((invoice().payments || []).length === 0) {
                  <p class="text-xs text-slate-400">No payments recorded yet.</p>
                } @else {
                  <div class="space-y-2">
                    @for (payment of invoice().payments; track payment.id) {
                      <div class="flex justify-between rounded-lg bg-slate-50 border border-slate-100 p-3 text-sm">
                        <span>{{ payment.payment_date | date:'dd MMM yyyy' }} &middot; {{ payment.method }}</span>
                        <span class="font-semibold text-emerald-700">{{ payment.amount | currency }}</span>
                      </div>
                    }
                  </div>
                }
              </section>
            </main>

            <aside class="space-y-4">
              <section class="bg-white rounded-xl border border-[#E7EEF5] p-4">
                <p class="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Due Date</p>
                <p class="text-sm font-semibold mt-1">{{ invoice().due_date | date:'dd MMM yyyy' }}</p>
                <div class="mt-4 pt-4 border-t border-slate-100 space-y-2 text-sm">
                  <div class="flex justify-between"><span class="text-slate-500">Total</span><span class="font-semibold">{{ invoice().total | currency }}</span></div>
                  <div class="flex justify-between"><span class="text-slate-500">Paid</span><span class="font-semibold text-emerald-700">{{ invoice().amount_paid | currency }}</span></div>
                  <div class="flex justify-between"><span class="text-slate-500">Adjustments</span><span class="font-semibold">{{ invoice().adjustment_total | currency }}</span></div>
                </div>
                <div class="mt-4 bg-[#14384F] text-white rounded-xl p-4 flex justify-between items-end">
                  <span class="text-xs uppercase tracking-wider text-slate-300">Balance Due</span>
                  <span class="text-2xl font-semibold">{{ invoice().balance_due | currency }}</span>
                </div>
              </section>
              <section class="bg-white rounded-xl border border-[#E7EEF5] p-4 text-sm text-slate-600">
                <p class="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Payment Notes</p>
                <p>{{ invoice().notes || invoice().payment_terms || 'Please use the invoice number as your payment reference.' }}</p>
              </section>
            </aside>
          </div>
        }
      </div>
    </div>
  `
})
export class CustomerInvoiceComponent implements OnInit {
  private api = inject(ApiService);
  private route = inject(ActivatedRoute);
  isLoading = signal(true);
  invoice = signal<any>(null);
  balance = computed(() => parseFloat(this.invoice()?.balance_due || 0));

  ngOnInit() {
    const token = this.route.snapshot.paramMap.get('token') || '';
    this.api.getPublicInvoice(token).subscribe({
      next: (res: any) => {
        this.invoice.set(res.success ? res.data.invoice : null);
        this.isLoading.set(false);
      },
      error: () => {
        this.invoice.set(null);
        this.isLoading.set(false);
      }
    });
  }
}
